/**
 * Where an opened app link lands — ipad-native-shell R16.2 / task 16.2.
 *
 * The shell's `PlatformAdapter.onAppUrlOpen` hands us the in-app path of a
 * universal link to one of our hosts (`/share/abc?x=1`). The SPA has no router:
 * `App.tsx` dispatches on `window.location.pathname` at mount. So the listener
 * navigates the bundled origin to the path this returns, and the app renders
 * whatever that path means — the same code a browser runs for the same link.
 *
 * Every link we send people is covered:
 *   - `/share/<id>` — a shared project (`App.tsx`'s `shareProjectIdFromPath`).
 *     Signed in, `App.tsx` turns it into `/?view=<id>` (`signedInShareTarget`)
 *     and the project opens in the signed-in viewer over the user's own town;
 *     signed out, it stays the anonymous share page.
 *   - the email links that `topLevelRoute.ts` already routes: invitations,
 *     verify-email, reset-password, account-delete confirm, privacy, terms
 *   - notification email links — `/notifications/link/<token>` and the
 *     `/world/notifications` they resolve to — open the notifications panel
 *     (`?open=notifications`, read by AppShell). The token only ever resolves to
 *     that panel (`channels/email.py`), so it is not round-tripped.
 *   - co-creation invites arrive as in-app notifications, not links, so the
 *     notifications panel covers them too.
 * Anything else lands on home — including `/demo`, which only ever redirects
 * to signup and means nothing inside the installed app. Never throws: a
 * link must not crash the app.
 */
import { getAccessToken, getNativeRefreshToken, isNativeShell } from "../api";
import { matchTopLevelRoute, shareProjectIdFromPath } from "../topLevelRoute";

export const NOTIFICATIONS_TARGET = "/?open=notifications";

/** `true` for the paths that mean "open the notifications panel". */
export function isNotificationsPath(pathname: string): boolean {
  const path = pathname.replace(/\/+$/, "");
  return path === "/world/notifications" || path.startsWith("/notifications/link/");
}

/** True in the app when someone is signed in. Read from the tokens, not the
 *  auth provider: the share route is decided before the provider mounts. The
 *  Keychain refresh token is loaded before first render (main.tsx), which is
 *  why the share decision is made at render in `App.tsx` and not in the link
 *  listener — on a cold launch the link can arrive before the Keychain read.
 *  The web page cannot tell (its session cookie belongs to the API host). */
export function signedInInApp(): boolean {
  return isNativeShell() && Boolean(getAccessToken() || getNativeRefreshToken());
}

/** The query key that asks the signed-in world to open a project in the viewer. */
export const VIEW_PROJECT_PARAM = "view";

/**
 * Operator ruling 2026-10-21: a share link opened inside the app by someone
 * signed in lands in the signed-in project viewer, in their own town — not on
 * the anonymous share page with its sign-up funnel. Returns where to go, or
 * null to keep the anonymous page (signed out, or not a share path). The read
 * is the same one a City project uses (`project_read_access.evaluate`: a
 * share link only exists for a `public` project, which any signed-in account
 * may read), so this widens nothing.
 */
export function signedInShareTarget(pathname: string, signedIn: boolean): string | null {
  if (!signedIn) return null;
  const id = shareProjectIdFromPath(pathname);
  if (!id) return null;
  return `/?${VIEW_PROJECT_PARAM}=${encodeURIComponent(id)}`;
}

/** A project id is a UUID. `?view=` is also the signed-out screen key
 *  (`/?view=login` after verifying an email, resetting a password or a dropped
 *  session), which the world read as a project id: "A friend's project" opened
 *  on `login` and polled /projects/login/* (prod validation sign-in, 2026-10-23). */
const PROJECT_ID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/** Read and clear `?view=<id>` from the URL the app opened with. Only a project
 *  id is taken; anything else is left for the screen it names. Never throws. */
export function takeViewedProject(): string | null {
  try {
    const id = new URLSearchParams(window.location.search).get(VIEW_PROJECT_PARAM);
    if (!id || !PROJECT_ID.test(id)) return null;
    window.history.replaceState(null, "", "/");
    return id;
  } catch {
    return null;
  }
}

export function appLinkTarget(inAppPath: string): string {
  try {
    const u = new URL(inAppPath, "https://app.invalid");
    if (u.origin !== "https://app.invalid") return "/";
    if (isNotificationsPath(u.pathname)) return NOTIFICATIONS_TARGET;
    if (shareProjectIdFromPath(u.pathname)) return `${u.pathname}${u.search}`;
    const route = matchTopLevelRoute(u.pathname);
    if (route !== "app" && route !== "demo") return `${u.pathname}${u.search}`;
    return "/";
  } catch {
    return "/";
  }
}

/**
 * Install the listener once at boot. Returns the adapter's unsubscribe.
 * `navigate` is injectable for tests; the default is a full navigation, which is
 * what the dispatch-at-mount SPA needs (and a link opened from outside the app
 * means it was backgrounded, which already ended any voice session, R3.6).
 */
export function installAppLinks(
  onAppUrlOpen: (handler: (path: string) => void) => () => void,
  navigate: (href: string) => void = (href) => window.location.assign(href),
): () => void {
  return onAppUrlOpen((path) => {
    navigate(appLinkTarget(path));
  });
}
